/**
 * Group Entity Schemas
 *
 * Types for groups and group membership.
 */

import { z } from 'zod'

/**
 * Group Source Schema
 */
export const GroupSourceSchema = z.enum([
  'PLATFORM_SECTION',
  'PLATFORM_ATHLETIC_TEAM',
  'PLATFORM_LIST',
  'SYSTEM',
  'CUSTOM'
])
export type GroupSource = z.infer<typeof GroupSourceSchema>

/**
 * Group sources whose membership is platform/section-managed and therefore
 * cannot be left by a member from the app.
 *
 * This is the canonical, single source of truth for the client-side leave
 * gate. It mirrors the backend's `PLATFORM_SYNCED_SOURCES` allowlist in
 * `apps/backend/src/api/groups/v1/use-cases/(members)/group-member-remove/use-case.ts`
 * VERBATIM — keep the two lists identical so the mobile gate and the backend
 * `PLATFORM_MEMBERSHIP_MANAGED` guard always agree.
 *
 * NOTE: these values intentionally key off the DB / backend `source` value
 * set, which drifts from `GroupSourceSchema` (the schema lacks
 * `PLATFORM_SCHOOL_LEVEL` / `PLATFORM_DEPARTMENT` / `PLATFORM_GRADE_LEVEL` /
 * `PLATFORM_SCHOOL` / `DYNAMIC`, and carries `PLATFORM_ATHLETIC_TEAM` / `SYSTEM`
 * which the DB does not). Reconciling that drift is deferred follow-up; do not
 * narrow this list to `GroupSourceSchema`.
 */
export const GROUP_MANAGED_MEMBERSHIP_SOURCES = [
  'PLATFORM_LIST',
  'PLATFORM_SCHOOL_LEVEL',
  'PLATFORM_DEPARTMENT',
  'PLATFORM_GRADE_LEVEL',
  'PLATFORM_SECTION',
  'PLATFORM_SCHOOL',
  'DYNAMIC'
] as const

/**
 * True when `source` identifies a platform/section-managed-membership group
 * (one a member cannot leave from the app). Tolerant of `undefined` / `null` /
 * empty / unknown values — all of which return `false` (status quo: treat as
 * leavable / undetectable).
 */
export function isManagedMembershipSource(source?: string | null): boolean {
  if (!source) return false
  return (GROUP_MANAGED_MEMBERSHIP_SOURCES as readonly string[]).includes(source)
}

/**
 * Group Member Role Schema
 */
export const GroupMemberRoleSchema = z.enum(['OWNER', 'ADMIN', 'MEMBER', 'VIEWER'])
export type GroupMemberRole = z.infer<typeof GroupMemberRoleSchema>

/**
 * Group Settings Schema
 */
export const GroupSettingsSchema = z.object({
  allowMemberPosts: z.boolean().optional(),
  allowMemberComments: z.boolean().optional(),
  allowMemberReactions: z.boolean().optional(),
  isPrivate: z.boolean().optional(),
  requireApproval: z.boolean().optional()
})

export type GroupSettings = z.infer<typeof GroupSettingsSchema>

/**
 * Group Member Schema
 */
export const GroupMemberSchema = z.object({
  id: z.string(),
  userId: z.string(),
  groupId: z.string(),
  role: GroupMemberRoleSchema,
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  photoUrl: z.string().nullable().optional(),
  joinedAt: z.string(),
  permissions: z.record(z.string(), z.boolean()).optional()
})

export type GroupMember = z.infer<typeof GroupMemberSchema>

/**
 * Group Schema
 */
export const GroupSchema = z.object({
  id: z.string(),
  name: z.string(),
  displayName: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  source: GroupSourceSchema,
  sourceEntityId: z.string().nullable().optional(),
  schoolId: z.string(),
  audience: z.string().nullable().optional(),
  memberCount: z.number().optional(),
  settings: GroupSettingsSchema.optional(),
  imageUrl: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string()
})

export type Group = z.infer<typeof GroupSchema>
